export type CaptionCue = { text: string; start: number; end: number };

const MIN_CUE_SECONDS = 1;
const MAX_CUE_SECONDS = 7;
const GAP_SECONDS = 0.15;

// No audio to time against here — captions are timed by estimated reading
// speed (words per minute), the same approach editors use for auto-generated
// "dummy" timing before fine-tuning by ear.
export function buildCues(lines: string[], wordsPerMinute: number): CaptionCue[] {
  let cursor = 0;
  return lines.map((text) => {
    const wordCount = Math.max(1, text.trim().split(/\s+/).filter(Boolean).length);
    const duration = Math.min(MAX_CUE_SECONDS, Math.max(MIN_CUE_SECONDS, (wordCount / wordsPerMinute) * 60));
    const start = cursor;
    const end = start + duration;
    cursor = end + GAP_SECONDS;
    return { text, start, end };
  });
}

function srtTime(seconds: number): string {
  const ms = Math.round(seconds * 1000);
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  const msRem = ms % 1000;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")},${String(msRem).padStart(3, "0")}`;
}

function vttTime(seconds: number): string {
  return srtTime(seconds).replace(",", ".");
}

function assTime(seconds: number): string {
  const cs = Math.round(seconds * 100);
  const h = Math.floor(cs / 360000);
  const m = Math.floor((cs % 360000) / 6000);
  const s = Math.floor((cs % 6000) / 100);
  const csRem = cs % 100;
  return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}.${String(csRem).padStart(2, "0")}`;
}

export function buildSrt(cues: CaptionCue[]): string {
  return cues.map((c, i) => `${i + 1}\n${srtTime(c.start)} --> ${srtTime(c.end)}\n${c.text}\n`).join("\n");
}

export function buildVtt(cues: CaptionCue[]): string {
  return `WEBVTT\n\n` + cues.map((c) => `${vttTime(c.start)} --> ${vttTime(c.end)}\n${c.text}\n`).join("\n");
}

// ASS/SSA is the format that actually carries the chosen font — most
// editors and players that support styled subtitles read Fontname from the
// Style line, unlike SRT/VTT which have no real font field at all.
export function buildAss(cues: CaptionCue[], fontName: string, fontSize = 28): string {
  const header = `[Script Info]
Title: Nohaya Caption Generator
ScriptType: v4.00+
WrapStyle: 0
ScaledBorderAndShadow: yes

[V4+ Styles]
Format: Name, Fontname, Fontsize, PrimaryColour, SecondaryColour, OutlineColour, BackColour, Bold, Italic, Underline, StrikeOut, ScaleX, ScaleY, Spacing, Angle, BorderStyle, Outline, Shadow, Alignment, MarginL, MarginR, MarginV, Encoding
Style: Default,${fontName},${fontSize},&H00FFFFFF,&H000000FF,&H00000000,&H64000000,0,0,0,0,100,100,0,0,1,2,0,2,10,10,10,1

[Events]
Format: Layer, Start, End, Style, Name, MarginL, MarginR, MarginV, Effect, Text
`;
  const events = cues.map((c) => `Dialogue: 0,${assTime(c.start)},${assTime(c.end)},Default,,0,0,0,,${c.text.replace(/\n/g, "\\N")}`).join("\n");
  return header + events + "\n";
}

export function downloadTextFile(filename: string, content: string) {
  // Some subtitle parsers (especially on Windows) default to the system
  // codepage instead of UTF-8 unless a BOM is present — without it,
  // non-Latin scripts (Tamil/Hindi/Telugu/Malayalam) can render as garbled
  // text even though the file itself is valid UTF-8.
  const blob = new Blob(["﻿" + content], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
